import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, X, Users as UsersIcon, KeyRound, Pencil, Check, Eye, PenSquare, Trash, ShieldCheck, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = {
  dashboard: "Dashboard",
  recepcion: "Recepción",
  laboratorio: "Laboratorio",
  correcciones: "Correcciones",
  produccion: "Producción",
  trazabilidad: "Trazabilidad",
  calculadora: "Calculadora IA",
  inventario: "Inventario",
  ventas: "Ventas",
  clientes: "Clientes",
  cobranza: "Cobranza",
  retornables: "Retornables",
  embarques: "Embarques",
  compras: "Compras",
  proveedores: "Proveedores",
  cuentas_por_pagar: "Cuentas por pagar",
  tesoreria: "Tesorería",
  checador: "Checador (Asistencia)",
  nomina: "Nómina",
  insumos: "Insumos",
  parametros: "Parámetros",
  usuarios: "Usuarios",
  auditoria: "Auditoría",
};

// Agrupación visual para la matriz (opcional, sólo etiqueta)
const MODULE_GROUPS = {
  "General": ["dashboard"],
  "Operación": ["recepcion", "laboratorio", "correcciones", "produccion", "trazabilidad", "calculadora"],
  "Almacén": ["inventario", "retornables", "insumos"],
  "Comercial": ["ventas", "clientes", "cobranza", "embarques"],
  "Financiero": ["tesoreria", "compras", "proveedores", "cuentas_por_pagar"],
  "Personal": ["checador", "nomina"],
  "Sistema": ["parametros", "usuarios", "auditoria"],
};

const ACTION_LABELS = { view: "Ver", create: "Crear", edit: "Editar", delete: "Eliminar" };
const ACTION_ICONS = { view: Eye, create: Plus, edit: PenSquare, delete: Trash };

const ROLES = [
  { v: "dueno", label: "Dueño (acceso total)" },
  { v: "admin", label: "Administrador" },
  { v: "laboratorio", label: "Laboratorio" },
  { v: "produccion", label: "Producción" },
  { v: "logistica", label: "Logística" },
  { v: "ventas", label: "Ventas" },
  { v: "cobranza", label: "Cobranza" },
];

export default function Usuarios() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState(["view", "create", "edit", "delete"]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [showFactoryReset, setShowFactoryReset] = useState(false);

  const load = async () => {
    const [u, m] = await Promise.all([api.get("/users"), api.get("/users/modules")]);
    setUsers(u.data);
    setModules(m.data.modules);
    setActions(m.data.actions);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="usuarios-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Usuarios y permisos</h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Crea usuarios, asigna permisos granulares por módulo. El rol <b>Dueño</b> tiene todos los permisos siempre.</p>
        </div>
        {!showForm && !editing && (
          <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-user-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Plus className="w-4 h-4 mr-1.5" /> Nuevo usuario
          </Button>
        )}
      </div>

      {(showForm || editing) && (
        <UserForm
          user={editing}
          modules={modules}
          actions={actions}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-stone-500" />
          <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Usuarios registrados</h3>
          <span className="text-xs text-stone-500 ml-auto">{users.length} · {users.filter((u) => u.active !== false).length} activos</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>{["Usuario", "Rol", "Estado", "Permisos habilitados", "Último acceso", "Acciones"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-stone-500">Sin usuarios registrados.</td></tr>
              ) : users.map((u) => {
                const grants = countGrants(u.permissions, modules);
                return (
                  <tr key={u.id} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-3">
                      <div className="font-medium text-stone-900 dark:text-stone-100">{u.name}</div>
                      <div className="text-xs text-stone-500 dark:text-stone-400">{u.email}</div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-outfit font-semibold ${u.role === "dueno" ? "bg-[#1E3A8A] text-white" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {u.active !== false
                        ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400"><Check className="w-3 h-3" /> Activo</span>
                        : <span className="text-xs text-stone-400">Inactivo</span>}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {u.role === "dueno"
                        ? <span className="text-[#1E3A8A] dark:text-blue-400 font-semibold flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Acceso total</span>
                        : <span className="text-stone-700 dark:text-stone-300 tabular-nums">{grants} / {modules.length * actions.length}</span>}
                    </td>
                    <td className="px-3 py-3 text-xs tabular-nums text-stone-600 dark:text-stone-400">{u.last_login_at ? u.last_login_at.slice(0, 19).replace("T", " ") : "—"}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditing(u)} title="Editar" data-testid={`edit-user-${u.id}`} className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setResetFor(u)} title="Resetear contraseña" className="p-1.5 rounded-md text-stone-500 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/40"><KeyRound className="w-4 h-4" /></button>
                        <button onClick={() => setToDelete(u)} title="Eliminar" className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar usuario {toDelete?.name}?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer. Sus registros históricos se conservan en la bitácora.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={async () => { try { await api.delete(`/users/${toDelete.id}`); toast.success("Usuario eliminado"); setToDelete(null); load(); } catch (err) { toast.error(err.response?.data?.detail || "Error"); } }} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ResetPasswordDialog user={resetFor} onClose={() => setResetFor(null)} onDone={() => { setResetFor(null); load(); }} />

      {(currentUser?.role === "dueno" || currentUser?.role === "admin") && (
        <Card className="p-5 border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20" data-testid="danger-zone-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-red-600 text-white flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <div className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Zona de peligro · Entregar sistema en ceros</div>
              <div className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                Borra TODA la información operativa (recepciones, laboratorio, producción, ventas, cobranza, compras, retornables, embarques, nómina, tesorería, proveedores, clientes, empleados, insumos y bitácora) para dejar el sistema listo para que un cliente nuevo capture datos reales. Se conserva únicamente tu cuenta ({currentUser?.email}); el resto de usuarios se elimina.
              </div>
              <div className="mt-3">
                <Button size="sm" onClick={() => setShowFactoryReset(true)} data-testid="factory-reset-open-btn" className="bg-red-600 hover:bg-red-700 text-white">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reiniciar sistema a ceros
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <FactoryResetDialog open={showFactoryReset} onClose={() => setShowFactoryReset(false)} />
    </div>
  );
}

const countGrants = (perms, modules) => {
  if (!perms) return 0;
  let n = 0;
  for (const m of modules) {
    for (const a of ["view", "create", "edit", "delete"]) {
      if (perms[m]?.[a]) n++;
    }
  }
  return n;
};

const UserForm = ({ user, modules, actions, onCancel, onSaved }) => {
  const isNew = !user;
  const [form, setForm] = useState(() => ({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    role: user?.role || "admin",
    active: user?.active !== false,
    permissions: user?.permissions || Object.fromEntries(modules.map((m) => [m, { view: false, create: false, edit: false, delete: false }])),
  }));

  const toggle = (module, action) => setForm((f) => ({
    ...f, permissions: { ...f.permissions, [module]: { ...(f.permissions[module] || {}), [action]: !f.permissions[module]?.[action] } },
  }));
  const toggleRow = (module) => {
    const cur = form.permissions[module] || {};
    const allOn = actions.every((a) => cur[a]);
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [module]: { ...cur, ...Object.fromEntries(actions.map((a) => [a, !allOn])) } } }));
  };
  const toggleCol = (action) => {
    const allOn = modules.every((m) => form.permissions[m]?.[action]);
    setForm((f) => ({ ...f, permissions: Object.fromEntries(modules.map((m) => [m, { ...(f.permissions[m] || {}), [action]: !allOn }])) }));
  };
  const grantAll = () => setForm((f) => ({ ...f, permissions: Object.fromEntries(modules.map((m) => [m, { ...(f.permissions[m] || {}), ...Object.fromEntries(actions.map((a) => [a, true])) }])) }));
  const clearAll = () => setForm((f) => ({ ...f, permissions: Object.fromEntries(modules.map((m) => [m, { ...(f.permissions[m] || {}), ...Object.fromEntries(actions.map((a) => [a, false])) }])) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Nombre y email requeridos");
    if (isNew && form.password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    try {
      const body = { name: form.name, email: form.email, role: form.role, active: form.active, permissions: form.permissions };
      if (isNew) {
        body.password = form.password;
        await api.post("/users", body);
        toast.success("Usuario creado");
      } else {
        await api.put(`/users/${user.id}`, body);
        toast.success("Usuario actualizado");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-outfit font-semibold text-lg text-stone-900 dark:text-stone-100">{isNew ? "Nuevo usuario" : `Editar ${user.name}`}</h2>
        <button onClick={onCancel} className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="user-name" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="user-email" /></div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Rol</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isNew && (
            <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Contraseña *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" placeholder="mín. 6 caracteres" data-testid="user-password" /></div>
          )}
          {!isNew && (
            <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-stone-700 dark:text-stone-300 cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Usuario activo</label></div>
          )}
        </div>

        {form.role === "dueno" ? (
          <div className="p-4 rounded-md bg-[#1E3A8A]/5 border border-[#1E3A8A]/20 flex items-center gap-2 text-sm">
            <ShieldCheck className="w-4 h-4 text-[#1E3A8A]" />
            <span className="text-stone-700 dark:text-stone-300">El rol <b>Dueño</b> tiene todos los permisos automáticamente. La matriz de abajo no aplica.</span>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Matriz de permisos</Label>
              <div className="flex gap-1">
                <button type="button" onClick={grantAll} className="text-[10px] px-2 py-1 rounded bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 font-outfit font-medium">Marcar todo</button>
                <button type="button" onClick={clearAll} className="text-[10px] px-2 py-1 rounded bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 font-outfit font-medium">Limpiar</button>
              </div>
            </div>
            <div className="border border-stone-200 dark:border-stone-800 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">Módulo</th>
                    {actions.map((a) => {
                      const Icon = ACTION_ICONS[a];
                      return (
                        <th key={a} className="px-3 py-2 text-center text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">
                          <button type="button" onClick={() => toggleCol(a)} className="inline-flex items-center gap-1 hover:text-[#1E3A8A]">
                            <Icon className="w-3 h-3" /> {ACTION_LABELS[a]}
                          </button>
                        </th>
                      );
                    })}
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(MODULE_GROUPS).map(([groupName, groupMods]) => {
                    const visibleMods = groupMods.filter((m) => modules.includes(m));
                    if (visibleMods.length === 0) return null;
                    return (
                      <React.Fragment key={groupName}>
                        <tr className="bg-stone-50 dark:bg-stone-900/60">
                          <td colSpan={actions.length + 2} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-outfit font-semibold text-stone-500 dark:text-stone-400 border-t border-stone-200 dark:border-stone-800">{groupName}</td>
                        </tr>
                        {visibleMods.map((m) => (
                          <tr key={m} className="border-b border-stone-100 dark:border-stone-800 last:border-0">
                            <td className="px-3 py-2 text-sm font-medium text-stone-800 dark:text-stone-200 pl-6">
                              {MODULE_LABELS[m] || m}
                              {m === "recepcion" && (
                                <label className="ml-2 inline-flex items-center gap-1 text-[10px] font-normal text-amber-700 dark:text-amber-500 cursor-pointer align-middle" title="Si está apagado, este usuario verá los lotes/litros/calidad pero NO los costos, forma de pago ni saldos de la leche">
                                  <input
                                    type="checkbox"
                                    data-testid="perm-recepcion-view_costs"
                                    checked={!!form.permissions.recepcion?.view_costs}
                                    onChange={() => toggle("recepcion", "view_costs")}
                                    className="w-3 h-3 rounded accent-amber-600 cursor-pointer"
                                  /> Ver costos
                                </label>
                              )}
                              {m === "checador" && (
                                <label className="ml-2 inline-flex items-center gap-1 text-[10px] font-normal text-amber-700 dark:text-amber-500 cursor-pointer align-middle" title="Si está encendido, esta cuenta solo podrá marcar asistencia con PIN (se oculta el registro manual por nombre). Útil para cuentas de iPad del checador.">
                                  <input
                                    type="checkbox"
                                    data-testid="perm-checador-pin_only"
                                    checked={!!form.permissions.checador?.pin_only}
                                    onChange={() => toggle("checador", "pin_only")}
                                    className="w-3 h-3 rounded accent-amber-600 cursor-pointer"
                                  /> Forzar solo PIN
                                </label>
                              )}
                            </td>
                            {actions.map((a) => (
                              <td key={a} className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  data-testid={`perm-${m}-${a}`}
                                  checked={!!form.permissions[m]?.[a]}
                                  onChange={() => toggle(m, a)}
                                  className="w-4 h-4 rounded accent-[#1E3A8A] cursor-pointer"
                                />
                              </td>
                            ))}
                            <td className="px-2 py-2 text-center">
                              <button type="button" onClick={() => toggleRow(m)} title="Marcar toda la fila" className="text-[10px] text-stone-500 hover:text-[#1E3A8A]">Fila</button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {/* Módulos sin grupo (por si backend agrega uno nuevo) */}
                  {modules.filter((m) => !Object.values(MODULE_GROUPS).flat().includes(m)).map((m) => (
                    <tr key={m} className="border-b border-stone-100 dark:border-stone-800 last:border-0">
                      <td className="px-3 py-2 text-sm font-medium text-stone-800 dark:text-stone-200">{MODULE_LABELS[m] || m}</td>
                      {actions.map((a) => (
                        <td key={a} className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            data-testid={`perm-${m}-${a}`}
                            checked={!!form.permissions[m]?.[a]}
                            onChange={() => toggle(m, a)}
                            className="w-4 h-4 rounded accent-[#1E3A8A] cursor-pointer"
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2 text-center">
                        <button type="button" onClick={() => toggleRow(m)} title="Marcar toda la fila" className="text-[10px] text-stone-500 hover:text-[#1E3A8A]">Fila</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
          <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button type="submit" data-testid="user-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">{isNew ? "Crear usuario" : "Guardar cambios"}</Button>
        </div>
      </form>
    </Card>
  );
};

const ResetPasswordDialog = ({ user, onClose, onDone }) => {
  const [pw, setPw] = useState("");
  useEffect(() => { setPw(""); }, [user]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Mínimo 6 caracteres");
    try {
      await api.put(`/users/${user.id}/password`, { new_password: pw });
      toast.success("Contraseña reseteada");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-outfit">Resetear contraseña</DialogTitle><DialogDescription>{user?.name} · {user?.email}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Nueva contraseña</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" placeholder="mín. 6 caracteres" autoFocus /></div>
          <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white">Resetear</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};


const CONFIRM_PHRASE = "BORRAR TODO";

const FactoryResetDialog = ({ open, onClose }) => {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setConfirmText(""); }, [open]);

  const submit = async () => {
    if (confirmText.trim().toUpperCase() !== CONFIRM_PHRASE) return toast.error(`Escribe exactamente "${CONFIRM_PHRASE}"`);
    setBusy(true);
    try {
      await api.post("/system/factory-reset", { confirm: confirmText });
      toast.success("Sistema reiniciado a ceros. Recargando…");
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reiniciar el sistema");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="factory-reset-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertTriangle className="w-4 h-4" /> Reiniciar sistema a ceros
          </DialogTitle>
          <DialogDescription>Esta acción NO se puede deshacer.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-stone-700 dark:text-stone-300 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md p-3">
            Se eliminará permanentemente: Recepciones, Laboratorio, Correcciones, Producción, Inventario, Ventas, Cobranza, Compras, Cuentas por pagar, Retornables, Embarques, Nómina completa (empleados, corridas, préstamos, bonos, asistencia), Tesorería (cuentas y movimientos), Proveedores, Clientes, Insumos y Bitácora/Auditoría. Todos los usuarios excepto tu cuenta actual serán eliminados.
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">
              Escribe <span className="font-mono text-red-700 dark:text-red-400">{CONFIRM_PHRASE}</span> para confirmar
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="mt-1.5 font-mono"
              placeholder={CONFIRM_PHRASE}
              autoFocus
              data-testid="factory-reset-confirm-input"
            />
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
            <Button
              type="button"
              onClick={submit}
              disabled={busy || confirmText.trim().toUpperCase() !== CONFIRM_PHRASE}
              data-testid="factory-reset-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {busy ? "Reiniciando…" : "Reiniciar sistema"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
